<template>
    <li
        class="vue-dnd-kanban-item"
        :class="{ 'vue-dnd-kanban-item-dragging': isDragging }"
        ref="elementRef"
        @pointerdown="handleDragStart"
    >
        <q-card
            class="kanban-card"
            :style="{ borderLeftColor: getColorValue(column.color) }"
        >
            <q-inner-loading :showing="isProcessing">
                <q-spinner size="24px" color="primary" />
            </q-inner-loading>

            <q-card-section class="kanban-card__content">
                <!-- Id del presupuesto -->
                <div class="row items-start q-gutter-xs">
                    <div class="col">
                        <h5 class="kanban-card__title" @click.stop="goToPresupuesto">
                            Presupuesto #{{ item.id }}
                        </h5>
                    </div>
                </div>

                <!-- Obra y Cliente -->
                <p class="kanban-card__description">
                    {{ item.obraReferencia }}<br>
                    <span class="kanban-card__cliente">{{ item.clienteNombre }}</span>
                </p>

                <!-- Divisor -->
                <q-separator class="q-my-xs" />

                <!-- Total y usuario -->
                <div class="row items-center justify-between q-gutter-xs">
                    <span class="kanban-card__amount">
                        {{ formatCurrency(item.total) }}
                    </span>
                    <div v-if="item.usuarioNombre" class="row items-center no-wrap kanban-card__usuario">
                        <q-avatar size="20px">
                            <img :src="`https://api.dicebear.com/9.x/identicon/svg?seed=${item.usuarioNombre}`" alt="Avatar" />
                        </q-avatar>
                        <span class="kanban-card__usuario-nombre">{{ item.usuarioNombre }}</span>
                    </div>
                </div>
            </q-card-section>
        </q-card>
    </li>
</template>

<script setup>
import { useDraggable } from '@vue-dnd-kit/core'
import { computed } from 'vue'
import numberFormat from '@/helpers/numberFormat'
import { useKanbanState } from '@/composables/useKanbanState'

const props = defineProps({
    item: {
        type: Object,
        required: true,
    },
    items: {
        type: Array,
        required: true,
    },
    itemIndex: {
        type: Number,
        required: true,
    },
    column: {
        type: Object,
        required: true,
    },
})

// Draggable para mover items
const { elementRef, handleDragStart, isDragging } = useDraggable({
    groups: ['kanban-column-body'],
    data: computed(() => ({
        source: props.items,
        index: props.itemIndex,
        itemId: props.item.id,
        sourceColumnId: props.column.id,
    })),
})

// Loading mientras se persiste el cambio de estado en el backend
const { isItemProcessing } = useKanbanState()
const isProcessing = computed(() => isItemProcessing(props.item.id))

// Navegar al detalle del presupuesto
const goToPresupuesto = () => {
    window.open(`/presupuestos/${props.item.id}`, '_blank')
}


// Mapear color a valor hexadecimal
const getColorValue = (color) => {
    const colorMap = {
        red: '#f44336',
        blue: '#2196f3',
        green: '#4caf50',
        purple: '#9c27b0',
        amber: '#ffc107',
        orange: '#ff9800',
        'deep-orange': '#ff5722',
        brown: '#795548',
    }
    return colorMap[color] || '#2196f3'
}

// Formatear total como moneda
const formatCurrency = (value) => {
    return numberFormat(value || 0)
}
</script>

<style lang="scss" scoped>
.vue-dnd-kanban-item {
    cursor: grab;
    transition: opacity 0.2s ease;
    list-style: none;

    &:active {
        cursor: grabbing;
    }

    &-dragging {
        opacity: 0.5;
    }

    &:hover {
        .kanban-card {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
        }
    }
}

.kanban-card {
    position: relative;
    border-left: 4px solid;
    border-radius: 8px !important;
    transition: box-shadow 0.2s ease;

    &__content {
        padding: 10px !important;
    }

    &__title {
        margin: 0;
        font-size: 12px;
        font-weight: 600;
        line-height: 1.3;
        color: #303030;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }

    &__protheus {
        margin: 0 !important;

        :deep(.q-chip__content) {
            font-size: 11px;
            font-weight: 600;
            padding: 0 4px;
        }
    }

    &__description {
        margin: 4px 0;
        font-size: 11px;
        color: #666;
        line-height: 1.3;
    }

    &__cliente {
        color: #999;
    }

    &__amount {
        font-size: 11px;
        font-weight: 600;
        color: #303030;
    }

    &__usuario {
        gap: 4px;
        min-width: 0;
    }

    &__usuario-nombre {
        font-size: 10px;
        color: #999;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 90px;
    }
}
</style>
